// frontend/src/contexts/NotificationContext.jsx
//
// Bell-icon notification state. One slim fetch:
//   - /api/notifications/badge/  → { health, pos, outstanding }
//
// Each signal is { count, has_issues, last_changed_at }:
//   - health      → setup-gap categories
//   - pos         → reorders to send today/overdue
//   - outstanding → outstanding deliveries due today/overdue
//
// TanStack-backed, with no Provider despite the file name. Polling,
// focus-refresh, network-reconnect-refresh, stale-response dropping, and
// unmount-safety are all handled by TanStack's defaults. The red dot clears the
// instant a write invalidates `notifications.badge`; the poll is the backstop
// for time-driven (clock) and other-actor (scheduled jobs, teammates)
// transitions that no local mutation can announce.
//
// Shape returned by useNotifications():
//   {
//     hasNotifications,
//     hasHealthIssues, healthIssueCount,
//     hasPOsDueToday, posDueTodayCount,
//     hasOutstandingDeliveries, outstandingDeliveriesCount,
//     fetchError,
//   }

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { sessionApi } from '../services/SessionApiService';
import { useAuth } from './AuthContext';
import { notifications as notificationsKey } from '../queries/keys';

const POLL_INTERVAL_MS = 60 * 1000;

async function fetchBadge() {
  const res = await sessionApi.get('/api/notifications/badge/');
  if (!res?.ok) throw new Error(`HTTP ${res?.status || 'unknown'}`);
  return res.json();
}

export function useNotifications() {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;

  const { data, isError } = useQuery({
    queryKey: notificationsKey.badge(storeId),
    queryFn: fetchBadge,
    refetchInterval: POLL_INTERVAL_MS,
    enabled: !!storeId,
  });

  return useMemo(() => {
    const health = data?.health;
    const pos = data?.pos;
    const outstanding = data?.outstanding;
    const hasHealthIssues = !!health?.has_issues;
    const hasPOsDueToday = !!pos?.has_issues;
    const hasOutstandingDeliveries = !!outstanding?.has_issues;
    return {
      hasNotifications: hasHealthIssues || hasPOsDueToday || hasOutstandingDeliveries,
      hasHealthIssues,
      healthIssueCount: health?.count || 0,
      hasPOsDueToday,
      posDueTodayCount: pos?.count || 0,
      hasOutstandingDeliveries,
      outstandingDeliveriesCount: outstanding?.count || 0,
      fetchError: isError ? 'badge' : null,
    };
  }, [data, isError]);
}
